import { ChevronDown, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '$lib/components/ui/Button';
import { Popover } from '$lib/components/ui/Popover';
import { cn } from '$lib/lib/cn';
import type { PipelineHealthPortal } from '$lib/api/adminScraper';
import { uiRunStatus } from '$lib/api/adminScraper';
import { severityFromStatus } from '$lib/components/ui/StatusDot';

export interface DashboardHeaderProps {
  portals: PipelineHealthPortal[];
  // Whether the dashboard is currently polling — surfaces a small "Live" hint
  // beside the title so an operator can tell the page is updating itself.
  isPolling: boolean;
  pollingIntervalSeconds: number;
  onStartScrape: (portalSlug: string) => void;
  // Default action — pick the operator-preferred default portal or run all.
  // Today the split button's primary click defers to the popover; this prop is
  // reserved so a future "all portals" sweep gets a hook without restructuring
  // the component.
  onStartScrapeDefault?: () => void;
}

// Roll-up: count portals by severity using the same StatusPill taxonomy the
// cards use. Without a shared taxonomy the header sentence and the cards
// drift apart in edge cases (e.g. a `paused` portal — cards mark it degraded,
// the sentence used to ignore it).
function countSeverities(portals: PipelineHealthPortal[]) {
  let healthy = 0;
  let degraded = 0;
  let failed = 0;
  for (const p of portals) {
    const sev = severityFromStatus(uiRunStatus(p.last_run_status ?? 'pending', p.error_count_last_run));
    if (sev === 'healthy') healthy += 1;
    else if (sev === 'degraded') degraded += 1;
    else if (sev === 'failed') failed += 1;
    // 'unknown' (never-run / pending / running / cancelled) is intentionally
    // not counted toward any of the three buckets — the sentence reads as a
    // health snapshot, and "running" isn't a health verdict.
  }
  return { healthy, degraded, failed, total: portals.length };
}

export function DashboardHeader({
  portals,
  isPolling,
  pollingIntervalSeconds,
  onStartScrape
}: DashboardHeaderProps) {
  const { t } = useTranslation();
  const counts = countSeverities(portals);
  const summary =
    counts.total > 0 && counts.degraded === 0 && counts.failed === 0 && counts.healthy === counts.total
      ? t('admin.dashboard.summaryAllHealthy', { total: counts.total })
      : t('admin.dashboard.summary', counts);

  return (
    <header
      data-testid="admin-header"
      className="flex shrink-0 items-center gap-3 border-b border-rule bg-surface-2 px-4 py-3"
    >
      <div className="flex min-w-0 flex-col">
        <div className="flex items-center gap-2">
          <h1 className="text-md font-semibold text-fg">{t('admin.dashboard.title')}</h1>
          {isPolling && (
            <span
              data-testid="admin-polling-indicator"
              className="inline-flex items-center gap-1 rounded-xs bg-info/12 px-1.5 py-0.5 text-2xs font-medium text-info"
            >
              <RefreshCw size={10} className="motion-safe:animate-spin" aria-hidden />
              {t('admin.dashboard.polling', { seconds: pollingIntervalSeconds })}
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-fg-muted" data-testid="admin-summary">
          {summary}
        </p>
      </div>

      <div className="ml-auto flex items-center gap-2">
        <Link
          to="/admin/scope"
          className="rounded border border-rule px-2 py-1 text-xs text-fg hover:bg-surface-2"
          data-testid="admin-header-scope-link"
        >
          Scope config
        </Link>
        <Link
          to="/admin/outliers"
          className="rounded border border-rule px-2 py-1 text-xs text-fg hover:bg-surface-2"
          data-testid="admin-header-outliers-link"
        >
          Outliers
        </Link>
        <SplitStartScrape portals={portals} onStartScrape={onStartScrape} />
      </div>
    </header>
  );
}

interface SplitStartScrapeProps {
  portals: PipelineHealthPortal[];
  onStartScrape: (portalSlug: string) => void;
}

// Split button: primary action opens the dropdown so the operator always
// picks a portal explicitly. Plan §4 specifies "Start scrape ▾ (dropdown for
// portal selection)" — keeping a single button surface keeps the header
// uncluttered and avoids an ambiguous "what does Start scrape do?" question.
function SplitStartScrape({ portals, onStartScrape }: SplitStartScrapeProps) {
  const { t } = useTranslation();
  return (
    <Popover
      align="end"
      trigger={
        <Button
          variant="primary"
          size="sm"
          data-testid="start-scrape-button"
          aria-label={t('admin.action.startScrape')}
        >
          {t('admin.action.startScrape')}
          <ChevronDown size={12} aria-hidden />
        </Button>
      }
    >
      {(close) => (
        <div role="menu" aria-label={t('admin.dashboard.portalMenuAriaLabel')} className="flex flex-col gap-1">
          {portals.map((p) => (
            <button
              key={p.slug}
              role="menuitem"
              data-testid={`start-scrape-${p.slug}`}
              className={cn(
                'flex items-center justify-between gap-3 rounded-xs px-2 py-1.5 text-left text-xs',
                'hover:bg-surface-2 focus:bg-surface-2 focus:outline-none'
              )}
              onClick={() => {
                onStartScrape(p.slug);
                close();
              }}
            >
              <span className="font-medium text-fg">{p.name}</span>
              <span className="text-2xs text-fg-subtle">
                {t('admin.action.startScrapeFor', { portal: p.name })}
              </span>
            </button>
          ))}
        </div>
      )}
    </Popover>
  );
}
